import { useEffect, useMemo, useState } from "react";
import axios from "axios";

/* ==============================
   CONFIG
============================== */
const BASE =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) ||
  "https://bmtvendorvendorfinal.onrender.com";
const APT_API = `${BASE}/api/vendor/apartment`;
const PLAN_API = `${BASE}/api/apartment-rate-plans`;
const DYN_API = `${BASE}/api/apartment-dynamic-pricing`;

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const MEAL_LABELS = {
  ROOM_ONLY: "Room only",
  BREAKFAST: "Breakfast included",
  BREAKFAST_DINNER: "Breakfast and dinner",
  ALL_MEALS: "All meals",
};
const CHARGE_LABELS = {
  NONE: "No charge",
  PERCENTAGE: "Percentage of booking",
  FIXED: "Fixed amount (INR)",
  ONE_NIGHT: "One night charge",
};
const TYPE_LABELS = {
  DATE_RANGE: "Date range",
  WEEKEND: "Weekends only (Fri, Sat)",
  FESTIVAL: "Festival",
  PEAK_SEASON: "Peak season",
  SPECIAL_EVENT: "Special event",
};

const EMPTY_PLAN = {
  name: "", code: "", description: "", mealPlan: "ROOM_ONLY",
  basePrice: "", weekendPrice: "", extraAdultPrice: "", extraChildPrice: "", extraMattressPrice: "",
  gstPercentage: "0",
  refundable: true, cancellationHours: "24", cancellationChargeType: "NONE", cancellationChargeValue: "0",
  minStay: "1", maxStay: "30", minAdvanceBookingHours: "0", maxAdvanceBookingDays: "365",
};
const EMPTY_RULE = { pricingType: "DATE_RANGE", title: "", price: "", priority: "1", minStay: "", maxStay: "", stopSell: false, notes: "" };

/* ==============================
   HELPERS
============================== */
const pad = (n) => String(n).padStart(2, "0");
const ymd = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const dateToStr = (t) => ymd(t.getFullYear(), t.getMonth(), t.getDate());
const todayStr = () => dateToStr(new Date());
const dayDiff = (a, b) => Math.round((new Date(`${b}T00:00:00Z`) - new Date(`${a}T00:00:00Z`)) / 86400000);
const rowDay = (v) => String(v).slice(0, 10);
const fmtDate = (ds) =>
  new Date(`${ds}T00:00:00Z`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "UTC" });
const inr = (n) =>
  n === undefined || n === null || n === ""
    ? "-"
    : new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n));
const numOr = (v, d = 0) => (v === "" || v === undefined || v === null ? d : Number(v));
const msgOf = (e, fallback) => e?.response?.data?.message || fallback;

const countApplicable = (start, end, weekendOnly) => {
  if (!start) return 0;
  const last = end || start;
  let count = 0;
  for (let i = 0; i <= dayDiff(start, last); i++) {
    const d = new Date(`${start}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + i);
    if (!weekendOnly || [5, 6].includes(d.getUTCDay())) count++;
  }
  return count;
};

const cancelText = (p) => {
  if (!p.refundable) return "Non-refundable";
  const h = p.cancellationHours;
  if (p.cancellationChargeType === "PERCENTAGE") return `${p.cancellationChargeValue}% charge within ${h}h of check-in`;
  if (p.cancellationChargeType === "FIXED") return `${inr(p.cancellationChargeValue)} charge within ${h}h of check-in`;
  if (p.cancellationChargeType === "ONE_NIGHT") return `One night charge within ${h}h of check-in`;
  return `Free cancellation up to ${h}h before check-in`;
};

const planToForm = (p) => ({
  name: p.name || "",
  code: p.code || "",
  description: p.description || "",
  mealPlan: p.mealPlan || "ROOM_ONLY",
  basePrice: String(p.basePrice ?? ""),
  weekendPrice: p.weekendPrice ? String(p.weekendPrice) : "",
  extraAdultPrice: p.extraAdultPrice ? String(p.extraAdultPrice) : "",
  extraChildPrice: p.extraChildPrice ? String(p.extraChildPrice) : "",
  extraMattressPrice: p.extraMattressPrice ? String(p.extraMattressPrice) : "",
  gstPercentage: String(p.gstPercentage ?? 0),
  refundable: p.refundable !== false,
  cancellationHours: String(p.cancellationHours ?? 24),
  cancellationChargeType: p.cancellationChargeType || "NONE",
  cancellationChargeValue: String(p.cancellationChargeValue ?? 0),
  minStay: String(p.minStay ?? 1),
  maxStay: String(p.maxStay ?? 30),
  minAdvanceBookingHours: String(p.minAdvanceBookingHours ?? 0),
  maxAdvanceBookingDays: String(p.maxAdvanceBookingDays ?? 365),
});

const validatePlan = (f) => {
  if (!f.name.trim()) return "Plan name is required.";
  if (!/^[A-Z0-9_-]{2,20}$/.test(f.code)) return "Code must be 2 to 20 characters: letters, numbers, - or _.";
  if (f.basePrice === "" || Number.isNaN(Number(f.basePrice)) || Number(f.basePrice) < 0) return "Enter a valid base price.";
  const money = [["weekendPrice", "Weekend price"], ["extraAdultPrice", "Extra adult price"], ["extraChildPrice", "Extra child price"], ["extraMattressPrice", "Extra mattress price"], ["cancellationHours", "Cancellation hours"], ["cancellationChargeValue", "Cancellation charge"], ["minAdvanceBookingHours", "Minimum advance hours"]];
  for (const [k, label] of money) {
    if (f[k] !== "" && (Number.isNaN(Number(f[k])) || Number(f[k]) < 0)) return `${label} cannot be negative.`;
  }
  if (f.gstPercentage !== "" && (Number(f.gstPercentage) < 0 || Number(f.gstPercentage) > 100)) return "GST must be between 0 and 100.";
  if (f.cancellationChargeType === "PERCENTAGE" && Number(f.cancellationChargeValue) > 100) return "Cancellation percentage cannot be more than 100.";
  for (const [k, label] of [["minStay", "Minimum stay"], ["maxStay", "Maximum stay"], ["maxAdvanceBookingDays", "Maximum advance days"]]) {
    const v = Number(f[k]);
    if (f[k] === "" || !Number.isInteger(v) || v < 1) return `${label} must be a whole number, at least 1.`;
  }
  if (Number(f.minStay) > Number(f.maxStay)) return "Minimum stay cannot be more than maximum stay.";
  return "";
};

const planPayload = (f) => ({
  name: f.name.trim(),
  code: f.code,
  description: f.description.trim(),
  mealPlan: f.mealPlan,
  basePrice: Number(f.basePrice),
  weekendPrice: numOr(f.weekendPrice),
  extraAdultPrice: numOr(f.extraAdultPrice),
  extraChildPrice: numOr(f.extraChildPrice),
  extraMattressPrice: numOr(f.extraMattressPrice),
  gstPercentage: numOr(f.gstPercentage),
  refundable: f.refundable,
  cancellationHours: numOr(f.cancellationHours),
  cancellationChargeType: f.cancellationChargeType,
  cancellationChargeValue: numOr(f.cancellationChargeValue),
  minStay: numOr(f.minStay, 1),
  maxStay: numOr(f.maxStay, 30),
  minAdvanceBookingHours: numOr(f.minAdvanceBookingHours),
  maxAdvanceBookingDays: numOr(f.maxAdvanceBookingDays, 365),
});

/* ==============================
   MAIN COMPONENT
============================== */
export default function VendorApartmentRatePlans() {
  const token = localStorage.getItem("vendorToken") || localStorage.getItem("token");
  const authHeaders = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const today = todayStr();

  // common
  const [screen, setScreen] = useState("plans"); // "plans" | "planForm" | "pricing"
  const [apartments, setApartments] = useState([]);
  const [aptId, setAptId] = useState(new URLSearchParams(window.location.search).get("apartment") || "");
  const [loadingApts, setLoadingApts] = useState(true);
  const [pageError, setPageError] = useState("");
  const [toast, setToast] = useState(null);

  // plans list
  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [deletePlan, setDeletePlan] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // plan form
  const [editingPlan, setEditingPlan] = useState(null);
  const [pf, setPf] = useState(EMPTY_PLAN);
  const [pfError, setPfError] = useState("");
  const [savingPlan, setSavingPlan] = useState(false);

  // date pricing
  const [activePlan, setActivePlan] = useState(null);
  const [rules, setRules] = useState([]);
  const [loadingRules, setLoadingRules] = useState(false);
  const [month, setMonth] = useState(() => ({ y: new Date().getFullYear(), m: new Date().getMonth() }));
  const [calMap, setCalMap] = useState({});
  const [loadingCal, setLoadingCal] = useState(false);
  const [priceKey, setPriceKey] = useState(0);
  const [range, setRange] = useState({ start: "", end: "" });
  const [rf, setRf] = useState(EMPTY_RULE);
  const [editingRule, setEditingRule] = useState(null);
  const [rfError, setRfError] = useState("");
  const [savingRule, setSavingRule] = useState(false);
  const [deleteRuleId, setDeleteRuleId] = useState(null);
  const [deletingRule, setDeletingRule] = useState(false);

  const showToast = (msg, type = "ok") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const apt = apartments.find((a) => a._id === aptId);
  const aptName = apt?.apartmentName || "";
  const planAptId = activePlan?.apartment || aptId;

  /* ---------- apartments ---------- */
  useEffect(() => {
    if (!token) {
      setLoadingApts(false);
      setPageError("Session not found. Please login again.");
      return;
    }
    (async () => {
      try {
        const res = await axios.get(APT_API, authHeaders);
        const list = res.data.data || [];
        setApartments(list);
        setAptId((cur) => (list.some((a) => a._id === cur) ? cur : list[0]?._id || ""));
      } catch (e) {
        setPageError(e.response?.status === 401 ? "Session expired. Please login again." : msgOf(e, "Failed to load apartments."));
      } finally {
        setLoadingApts(false);
      }
    })();
  }, [token, authHeaders]);

  /* ---------- rate plans ---------- */
  useEffect(() => {
    if (!aptId) return;
    let cancelled = false;
    (async () => {
      setLoadingPlans(true);
      setPageError("");
      try {
        const res = await axios.get(`${PLAN_API}/apartment/${aptId}`, authHeaders);
        if (!cancelled) setPlans(res.data.data || []);
      } catch (e) {
        if (!cancelled) setPageError(msgOf(e, "Failed to load rate plans."));
      } finally {
        if (!cancelled) setLoadingPlans(false);
      }
    })();
    return () => { cancelled = true; };
  }, [aptId, reloadKey, authHeaders]);

  useEffect(() => { window.scrollTo({ top: 0 }); }, [screen]);

  /* ---------- date price rules (pricing screen) ---------- */
  useEffect(() => {
    if (screen !== "pricing" || !activePlan) return;
    let cancelled = false;
    (async () => {
      setLoadingRules(true);
      try {
        const res = await axios.get(`${DYN_API}/apartment/${planAptId}/rate-plan/${activePlan._id}`, authHeaders);
        if (!cancelled) setRules(res.data.data || []);
      } catch (e) {
        if (!cancelled) showToast(msgOf(e, "Failed to load date prices."), "err");
      } finally {
        if (!cancelled) setLoadingRules(false);
      }
    })();
    return () => { cancelled = true; };
  }, [screen, activePlan, planAptId, priceKey, authHeaders]);

  const daysInMonth = new Date(month.y, month.m + 1, 0).getDate();
  const firstDow = new Date(month.y, month.m, 1).getDay();

  useEffect(() => {
    if (screen !== "pricing" || !activePlan) return;
    let cancelled = false;
    (async () => {
      setLoadingCal(true);
      try {
        const res = await axios.get(`${DYN_API}/apartment/${planAptId}/rate-plan/${activePlan._id}/calendar`, {
          ...authHeaders,
          params: { from: ymd(month.y, month.m, 1), to: ymd(month.y, month.m, daysInMonth) },
        });
        if (cancelled) return;
        const map = {};
        (res.data.data || []).forEach((r) => { map[r.date] = r; });
        setCalMap(map);
      } catch {
        if (!cancelled) setCalMap({});
      } finally {
        if (!cancelled) setLoadingCal(false);
      }
    })();
    return () => { cancelled = true; };
  }, [screen, activePlan, planAptId, month, daysInMonth, priceKey, authHeaders]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (deletePlan) return !deleting && setDeletePlan(null);
      if (screen === "planForm" && !savingPlan) setScreen("plans");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [deletePlan, deleting, screen, savingPlan]);

  /* ==============================
     PLAN ACTIONS
  ============================== */
  const setP = (k, v) => setPf((p) => ({ ...p, [k]: v }));

  const openAddPlan = () => {
    setEditingPlan(null);
    setPf({ ...EMPTY_PLAN, basePrice: apt?.pricing?.basePrice ? String(apt.pricing.basePrice) : "" });
    setPfError("");
    setScreen("planForm");
  };

  const openEditPlan = (p) => {
    setEditingPlan(p);
    setPf(planToForm(p));
    setPfError("");
    setScreen("planForm");
  };

  const savePlan = async (e) => {
    e.preventDefault();
    const problem = validatePlan(pf);
    if (problem) return setPfError(problem);

    setSavingPlan(true);
    setPfError("");
    try {
      const body = planPayload(pf);
      if (editingPlan) await axios.put(`${PLAN_API}/${editingPlan._id}`, body, authHeaders);
      else await axios.post(`${PLAN_API}/apartment/${aptId}`, body, authHeaders);
      showToast(editingPlan ? "Rate plan updated." : "Rate plan created.");
      setReloadKey((k) => k + 1);
      setScreen("plans");
    } catch (err) {
      setPfError(msgOf(err, "Could not save the rate plan. Please try again."));
    } finally {
      setSavingPlan(false);
    }
  };

  const togglePlan = async (p) => {
    try {
      const res = await axios.patch(`${PLAN_API}/${p._id}/toggle`, {}, authHeaders);
      const updated = res.data.data;
      setPlans((list) => list.map((x) => (x._id === p._id ? { ...x, ...updated } : x)));
      showToast(updated.isActive ? "Rate plan is active." : "Rate plan is inactive.");
    } catch (err) {
      showToast(msgOf(err, "Could not change status."), "err");
    }
  };

  const confirmDeletePlan = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${PLAN_API}/${deletePlan._id}`, authHeaders);
      setPlans((list) => list.filter((x) => x._id !== deletePlan._id));
      setDeletePlan(null);
      showToast("Rate plan deleted.");
    } catch (err) {
      showToast(msgOf(err, "Delete failed."), "err");
    } finally {
      setDeleting(false);
    }
  };

  /* ==============================
     DATE PRICING ACTIONS
  ============================== */
  const resetRuleForm = () => {
    setRange({ start: "", end: "" });
    setRf(EMPTY_RULE);
    setEditingRule(null);
    setRfError("");
  };

  const openPricing = (p) => {
    const n = new Date();
    setActivePlan(p);
    setRules([]);
    setCalMap({});
    setDeleteRuleId(null);
    resetRuleForm();
    setMonth({ y: n.getFullYear(), m: n.getMonth() });
    setPriceKey((k) => k + 1);
    setScreen("pricing");
  };

  const closePricing = () => {
    setScreen("plans");
    setReloadKey((k) => k + 1); // rules count refresh
  };

  const shiftMonth = (d) =>
    setMonth(({ y, m }) => {
      const n = new Date(y, m + d, 1);
      return { y: n.getFullYear(), m: n.getMonth() };
    });
  const goToday = () => { const n = new Date(); setMonth({ y: n.getFullYear(), m: n.getMonth() }); };

  const endDate = range.end || range.start;
  const inRange = (ds) => !!range.start && ds >= range.start && ds <= endDate;
  const setR = (k, v) => setRf((p) => ({ ...p, [k]: v }));

  const onDayClick = (ds) => {
    if (ds < today) return;
    setRfError("");
    if (!range.start || range.end) {
      setRange({ start: ds, end: "" });
      if (!editingRule) setRf((p) => (p.price === "" && calMap[ds] ? { ...p, price: String(calMap[ds].price) } : p));
      return;
    }
    if (ds < range.start) setRange({ start: ds, end: range.start });
    else setRange({ start: range.start, end: ds });
  };

  const pickMonth = () =>
    setRange({
      start: ymd(month.y, month.m, 1) < today ? today : ymd(month.y, month.m, 1),
      end: ymd(month.y, month.m, daysInMonth),
    });

  const editRule = (r) => {
    const s = rowDay(r.startDate);
    const e = rowDay(r.endDate);
    setEditingRule(r);
    setRange({ start: s, end: e === s ? "" : e });
    setRf({
      pricingType: r.pricingType || "DATE_RANGE",
      title: r.title || "",
      price: String(r.price ?? ""),
      priority: String(r.priority ?? 1),
      minStay: r.minStay != null ? String(r.minStay) : "",
      maxStay: r.maxStay != null ? String(r.maxStay) : "",
      stopSell: !!r.stopSell,
      notes: r.notes || "",
    });
    setMonth({ y: Number(s.slice(0, 4)), m: Number(s.slice(5, 7)) - 1 });
    setRfError("");
    setTimeout(() => document.getElementById("rp-rule-form")?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  const validateRule = () => {
    if (!range.start) return "Pick dates on the calendar or enter a start date.";
    if (endDate < range.start) return "End date cannot be before the start date.";
    if (!editingRule && range.start < today) return "Start date cannot be in the past.";
    if (dayDiff(range.start, endDate) + 1 > 731) return "One rule can cover at most 731 days.";
    if (rf.price === "" || Number.isNaN(Number(rf.price)) || Number(rf.price) < 0) return "Enter a valid price per night.";
    for (const [k, label] of [["minStay", "Minimum stay"], ["maxStay", "Maximum stay"]]) {
      if (rf[k] !== "" && (!Number.isInteger(Number(rf[k])) || Number(rf[k]) < 1)) return `${label} must be a whole number, at least 1.`;
    }
    if (rf.minStay !== "" && rf.maxStay !== "" && Number(rf.minStay) > Number(rf.maxStay)) return "Minimum stay cannot be more than maximum stay.";
    const pr = Number(rf.priority);
    if (rf.priority === "" || !Number.isInteger(pr) || pr < 1 || pr > 100) return "Priority must be a whole number from 1 to 100.";
    return "";
  };

  const saveRule = async (e) => {
    e.preventDefault();
    const problem = validateRule();
    if (problem) return setRfError(problem);

    const body = {
      startDate: range.start,
      endDate,
      pricingType: rf.pricingType,
      title: rf.title.trim(),
      price: Number(rf.price),
      minStay: rf.minStay === "" ? null : Number(rf.minStay),
      maxStay: rf.maxStay === "" ? null : Number(rf.maxStay),
      stopSell: rf.stopSell,
      priority: Number(rf.priority),
      notes: rf.notes.trim(),
    };

    setSavingRule(true);
    setRfError("");
    try {
      if (editingRule) await axios.put(`${DYN_API}/${editingRule._id}`, body, authHeaders);
      else await axios.post(`${DYN_API}/apartment/${planAptId}/rate-plan/${activePlan._id}`, body, authHeaders);
      showToast(editingRule ? "Date price updated." : "Date price saved.");
      resetRuleForm();
      setPriceKey((k) => k + 1);
    } catch (err) {
      setRfError(msgOf(err, "Could not save the date price. Please try again."));
    } finally {
      setSavingRule(false);
    }
  };

  const toggleRule = async (r) => {
    try {
      const res = await axios.patch(`${DYN_API}/${r._id}/toggle`, {}, authHeaders);
      const updated = res.data.data;
      setRules((list) => list.map((x) => (x._id === r._id ? updated : x)));
      setPriceKey((k) => k + 1);
    } catch (err) {
      showToast(msgOf(err, "Could not change status."), "err");
    }
  };

  const confirmDeleteRule = async (id) => {
    setDeletingRule(true);
    try {
      await axios.delete(`${DYN_API}/${id}`, authHeaders);
      setRules((list) => list.filter((x) => x._id !== id));
      if (editingRule?._id === id) resetRuleForm();
      setDeleteRuleId(null);
      setPriceKey((k) => k + 1);
      showToast("Date price deleted.");
    } catch (err) {
      showToast(msgOf(err, "Delete failed."), "err");
    } finally {
      setDeletingRule(false);
    }
  };

  /* ---------- calendar cells ---------- */
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(<div key={`b${i}`} className="rp-cell blank" />);
  for (let d = 1; d <= daysInMonth; d++) {
    const ds = ymd(month.y, month.m, d);
    const info = calMap[ds];
    const past = ds < today;
    let state = "none";
    if (info) state = info.stopSell ? "stop" : info.source;
    cells.push(
      <button
        key={ds}
        type="button"
        disabled={past}
        onClick={() => onDayClick(ds)}
        className={`rp-cell ${state} ${past ? "past" : ""} ${inRange(ds) ? "sel" : ""} ${range.start === ds || (range.start && endDate === ds) ? "edge" : ""} ${ds === today ? "today" : ""}`}
        aria-pressed={inRange(ds)}
        aria-label={`${d} ${MONTHS[month.m]}`}
      >
        <span className="rp-day">{d}</span>
        {info ? (
          <>
            <span className="rp-cp">{info.stopSell ? "Stopped" : inr(info.price)}</span>
            {info.title ? <span className="rp-ct">{info.title}</span> : null}
          </>
        ) : (
          <span className="rp-ct">-</span>
        )}
      </button>
    );
  }

  const applyCount = countApplicable(range.start, endDate, rf.pricingType === "WEEKEND");

  /* ==============================
     JSX
  ============================== */
  return (
    <div className="rp">
      <style>{css}</style>

      {/* ============ PLANS LIST ============ */}
      {screen === "plans" && (
        <>
          <header className="rp-head">
            <div>
              <h1>Rate plans</h1>
              <p>Create pricing plans for your apartment, then set special prices for dates like weekends and festivals.</p>
            </div>
            {apartments.length > 0 && (
              <button type="button" className="rp-btn rp-main rp-add" onClick={openAddPlan}>+ Add rate plan</button>
            )}
          </header>

          {loadingApts ? (
            <div className="rp-state"><div className="rp-spin" /></div>
          ) : apartments.length === 0 ? (
            <div className="rp-state"><p>{pageError || "You have no apartments yet. Add an apartment first."}</p></div>
          ) : (
            <>
              <section className="rp-card rp-toolbar">
                <div className="rp-field rp-grow">
                  <label htmlFor="rp-apt">Apartment</label>
                  <select id="rp-apt" value={aptId} onChange={(e) => setAptId(e.target.value)}>
                    {apartments.map((a) => (
                      <option key={a._id} value={a._id}>
                        {a.apartmentName}{a.city ? ` - ${a.city}` : ""}{a.status === "inactive" ? " (inactive)" : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="rp-mini">
                  <span>{plans.length}</span>rate plans
                </div>
                <div className="rp-mini">
                  <span>{inr(apt?.pricing?.basePrice)}</span>apartment base price
                </div>
              </section>

              {pageError && (
                <div className="rp-error" role="alert">
                  {pageError} <button type="button" className="rp-btn sm" onClick={() => setReloadKey((k) => k + 1)}>Try again</button>
                </div>
              )}

              {loadingPlans ? (
                <div className="rp-state"><div className="rp-spin" /></div>
              ) : plans.length === 0 ? (
                <div className="rp-state">
                  <div className="rp-ico">₹</div>
                  <p>{aptName || "This apartment"} has no rate plans yet.</p>
                  <button type="button" className="rp-btn rp-main" onClick={openAddPlan}>Create first rate plan</button>
                </div>
              ) : (
                <div className="rp-plans">
                  {plans.map((p) => (
                    <article key={p._id} className={`rp-plan ${p.isActive ? "" : "off"}`}>
                      <div className="rp-plan-top">
                        <div className="rp-plan-title">
                          <h3 title={p.name}>{p.name}</h3>
                          <span className="rp-code">{p.code}</span>
                          {!p.isActive && <span className="rp-off">Inactive</span>}
                        </div>
                        <label className="rp-switch" title={p.isActive ? "Click to deactivate" : "Click to activate"}>
                          <input type="checkbox" checked={!!p.isActive} onChange={() => togglePlan(p)} />
                          <span className="rp-track" />
                        </label>
                      </div>

                      <div className="rp-price">
                        <strong>{inr(p.basePrice)}</strong><span>per night</span>
                        {p.weekendPrice > 0 && <em>Weekend {inr(p.weekendPrice)}</em>}
                      </div>

                      <ul className="rp-facts">
                        <li>{MEAL_LABELS[p.mealPlan] || p.mealPlan}</li>
                        <li>{p.minStay} to {p.maxStay} nights</li>
                        {p.gstPercentage > 0 && <li>GST {p.gstPercentage}%</li>}
                        {p.extraAdultPrice > 0 && <li>Extra adult {inr(p.extraAdultPrice)}</li>}
                        {p.extraChildPrice > 0 && <li>Extra child {inr(p.extraChildPrice)}</li>}
                        {p.extraMattressPrice > 0 && <li>Mattress {inr(p.extraMattressPrice)}</li>}
                      </ul>
                      <p className="rp-cancel">{cancelText(p)}</p>
                      {p.description && <p className="rp-desc">{p.description}</p>}

                      <div className="rp-plan-foot">
                        <button type="button" className="rp-btn rp-main sm" onClick={() => openPricing(p)}>
                          Date prices{p.rulesCount ? ` (${p.rulesCount})` : ""}
                        </button>
                        <div className="rp-acts">
                          <button type="button" className="rp-btn sm" onClick={() => openEditPlan(p)}>Edit</button>
                          <button type="button" className="rp-btn sm danger" onClick={() => setDeletePlan(p)}>Delete</button>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ============ PLAN FORM ============ */}
      {screen === "planForm" && (
        <div className="rp-wrap">
          <button type="button" className="rp-back" onClick={() => !savingPlan && setScreen("plans")}>&larr; Back to rate plans</button>

          <form className="rp-card" onSubmit={savePlan} noValidate>
            <h1 className="rp-title">{editingPlan ? "Edit rate plan" : "Add rate plan"}</h1>
            <p className="rp-hint">{aptName}</p>

            {pfError && <div className="rp-error" role="alert">{pfError}</div>}

            <h2 className="rp-sec">Basics</h2>
            <div className="rp-grid">
              <div className="rp-field">
                <label htmlFor="pf-name">Plan name *</label>
                <input id="pf-name" type="text" value={pf.name} placeholder="e.g., Standard with breakfast" onChange={(e) => setP("name", e.target.value)} />
              </div>
              <div className="rp-field">
                <label htmlFor="pf-code">Code * (letters, numbers, - _)</label>
                <input id="pf-code" type="text" value={pf.code} placeholder="e.g., STD-BF" maxLength={20} onChange={(e) => setP("code", e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ""))} />
              </div>
              <div className="rp-field">
                <label htmlFor="pf-meal">Meal plan</label>
                <select id="pf-meal" value={pf.mealPlan} onChange={(e) => setP("mealPlan", e.target.value)}>
                  {Object.entries(MEAL_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div className="rp-field">
                <label htmlFor="pf-desc">Description</label>
                <input id="pf-desc" type="text" value={pf.description} placeholder="Shown to guests (optional)" onChange={(e) => setP("description", e.target.value)} />
              </div>
            </div>

            <h2 className="rp-sec">Prices (INR)</h2>
            <div className="rp-grid three">
              <div className="rp-field">
                <label htmlFor="pf-base">Base price per night *</label>
                <input id="pf-base" type="number" min="0" value={pf.basePrice} placeholder="e.g., 3000" onChange={(e) => setP("basePrice", e.target.value)} />
              </div>
              <div className="rp-field">
                <label htmlFor="pf-wk">Weekend price (Fri, Sat)</label>
                <input id="pf-wk" type="number" min="0" value={pf.weekendPrice} placeholder="Leave empty to use base" onChange={(e) => setP("weekendPrice", e.target.value)} />
              </div>
              <div className="rp-field">
                <label htmlFor="pf-gst">GST %</label>
                <input id="pf-gst" type="number" min="0" max="100" value={pf.gstPercentage} onChange={(e) => setP("gstPercentage", e.target.value)} />
              </div>
              <div className="rp-field">
                <label htmlFor="pf-ea">Extra adult</label>
                <input id="pf-ea" type="number" min="0" value={pf.extraAdultPrice} placeholder="per night" onChange={(e) => setP("extraAdultPrice", e.target.value)} />
              </div>
              <div className="rp-field">
                <label htmlFor="pf-ec">Extra child</label>
                <input id="pf-ec" type="number" min="0" value={pf.extraChildPrice} placeholder="per night" onChange={(e) => setP("extraChildPrice", e.target.value)} />
              </div>
              <div className="rp-field">
                <label htmlFor="pf-em">Extra mattress</label>
                <input id="pf-em" type="number" min="0" value={pf.extraMattressPrice} placeholder="per night" onChange={(e) => setP("extraMattressPrice", e.target.value)} />
              </div>
            </div>

            <h2 className="rp-sec">Cancellation</h2>
            <label className={`rp-check ${pf.refundable ? "on" : ""}`}>
              <input type="checkbox" checked={pf.refundable} onChange={(e) => setP("refundable", e.target.checked)} />
              Refundable plan
            </label>
            {pf.refundable && (
              <div className="rp-grid three" style={{ marginTop: 14 }}>
                <div className="rp-field">
                  <label htmlFor="pf-ch">Free until (hours before check-in)</label>
                  <input id="pf-ch" type="number" min="0" value={pf.cancellationHours} onChange={(e) => setP("cancellationHours", e.target.value)} />
                </div>
                <div className="rp-field">
                  <label htmlFor="pf-ct">Charge after that</label>
                  <select id="pf-ct" value={pf.cancellationChargeType} onChange={(e) => setP("cancellationChargeType", e.target.value)}>
                    {Object.entries(CHARGE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                {(pf.cancellationChargeType === "PERCENTAGE" || pf.cancellationChargeType === "FIXED") && (
                  <div className="rp-field">
                    <label htmlFor="pf-cv">{pf.cancellationChargeType === "PERCENTAGE" ? "Charge %" : "Charge amount"}</label>
                    <input id="pf-cv" type="number" min="0" value={pf.cancellationChargeValue} onChange={(e) => setP("cancellationChargeValue", e.target.value)} />
                  </div>
                )}
              </div>
            )}

            <h2 className="rp-sec">Booking rules</h2>
            <div className="rp-grid four">
              <div className="rp-field">
                <label htmlFor="pf-min">Min nights</label>
                <input id="pf-min" type="number" min="1" value={pf.minStay} onChange={(e) => setP("minStay", e.target.value)} />
              </div>
              <div className="rp-field">
                <label htmlFor="pf-max">Max nights</label>
                <input id="pf-max" type="number" min="1" value={pf.maxStay} onChange={(e) => setP("maxStay", e.target.value)} />
              </div>
              <div className="rp-field">
                <label htmlFor="pf-mah">Book at least (hours ahead)</label>
                <input id="pf-mah" type="number" min="0" value={pf.minAdvanceBookingHours} onChange={(e) => setP("minAdvanceBookingHours", e.target.value)} />
              </div>
              <div className="rp-field">
                <label htmlFor="pf-mad">Book at most (days ahead)</label>
                <input id="pf-mad" type="number" min="1" value={pf.maxAdvanceBookingDays} onChange={(e) => setP("maxAdvanceBookingDays", e.target.value)} />
              </div>
            </div>

            <div className="rp-foot">
              <button type="button" className="rp-btn" onClick={() => setScreen("plans")} disabled={savingPlan}>Cancel</button>
              <button type="submit" className="rp-btn rp-main" disabled={savingPlan}>
                {savingPlan ? "Saving..." : editingPlan ? "Update rate plan" : "Create rate plan"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ============ DATE PRICING ============ */}
      {screen === "pricing" && activePlan && (
        <div className="rp-wrap wide">
          <button type="button" className="rp-back" onClick={closePricing}>&larr; Back to rate plans</button>

          <div className="rp-card">
            <div className="rp-plan-top">
              <div>
                <h1 className="rp-title">{activePlan.name} <span className="rp-code">{activePlan.code}</span></h1>
                <p className="rp-hint">{aptName}. Normal price {inr(activePlan.basePrice)}{activePlan.weekendPrice > 0 ? `, weekend ${inr(activePlan.weekendPrice)}` : ""}. Click dates below to set a special price.</p>
              </div>
              {!activePlan.isActive && <span className="rp-off">Plan inactive</span>}
            </div>
          </div>

          <section className="rp-card">
            <div className="rp-cal-top">
              <div className="rp-monthnav">
                <button type="button" className="rp-btn" onClick={() => shiftMonth(-1)} aria-label="Previous month">&lsaquo;</button>
                <strong>{MONTHS[month.m]} {month.y}</strong>
                <button type="button" className="rp-btn" onClick={() => shiftMonth(1)} aria-label="Next month">&rsaquo;</button>
                <button type="button" className="rp-btn soft" onClick={goToday}>Today</button>
              </div>
              <div className="rp-legend">
                <i className="lg base" />Base
                <i className="lg weekend" />Weekend
                <i className="lg rule" />Special price
                <i className="lg stop" />Stopped
              </div>
            </div>
            <p className="rp-hint">For one day, click it. For a range, click the first day and then the last day.</p>
            <div className={`rp-cal ${loadingCal ? "loading" : ""}`}>
              {DOW.map((d) => <div key={d} className="rp-dow">{d}</div>)}
              {cells}
            </div>
            <div className="rp-cal-acts">
              <button type="button" className="rp-btn sm" onClick={pickMonth}>Select whole month</button>
              <button type="button" className="rp-btn sm" onClick={resetRuleForm} disabled={!range.start && !editingRule}>Clear selection</button>
            </div>
          </section>

          <form id="rp-rule-form" className="rp-card" onSubmit={saveRule} noValidate>
            <h2 className="rp-sec first">{editingRule ? "Edit date price" : "Set date price"}</h2>
            {editingRule && <p className="rp-hint">You are editing an existing date price. Clear the selection to add a new one instead.</p>}
            {rfError && <div className="rp-error" role="alert">{rfError}</div>}

            <div className="rp-grid">
              <div className="rp-field">
                <label htmlFor="rf-start">Start date</label>
                <input id="rf-start" type="date" value={range.start} onChange={(e) => setRange((p) => ({ ...p, start: e.target.value }))} />
              </div>
              <div className="rp-field">
                <label htmlFor="rf-end">End date (empty = only start day)</label>
                <input id="rf-end" type="date" min={range.start || undefined} value={range.end} onChange={(e) => setRange((p) => ({ ...p, end: e.target.value }))} />
              </div>
            </div>

            <div className="rp-grid three">
              <div className="rp-field">
                <label htmlFor="rf-price">Price per night (INR) *</label>
                <input id="rf-price" type="number" min="0" value={rf.price} placeholder={`Normal ${activePlan.basePrice}`} onChange={(e) => setR("price", e.target.value)} />
              </div>
              <div className="rp-field">
                <label htmlFor="rf-type">Applies to</label>
                <select id="rf-type" value={rf.pricingType} onChange={(e) => setR("pricingType", e.target.value)}>
                  {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div className="rp-field">
                <label htmlFor="rf-title">Label (optional)</label>
                <input id="rf-title" type="text" maxLength={80} value={rf.title} placeholder="e.g., Diwali" onChange={(e) => setR("title", e.target.value)} />
              </div>
              <div className="rp-field">
                <label htmlFor="rf-min">Min nights (optional)</label>
                <input id="rf-min" type="number" min="1" value={rf.minStay} placeholder={`Plan: ${activePlan.minStay}`} onChange={(e) => setR("minStay", e.target.value)} />
              </div>
              <div className="rp-field">
                <label htmlFor="rf-max">Max nights (optional)</label>
                <input id="rf-max" type="number" min="1" value={rf.maxStay} placeholder={`Plan: ${activePlan.maxStay}`} onChange={(e) => setR("maxStay", e.target.value)} />
              </div>
              <div className="rp-field">
                <label htmlFor="rf-pri">Priority (1 to 100)</label>
                <input id="rf-pri" type="number" min="1" max="100" value={rf.priority} onChange={(e) => setR("priority", e.target.value)} />
              </div>
            </div>

            <p className="rp-hint">If two date prices overlap on a day, the one with the higher priority wins.</p>

            <div className="rp-grid">
              <label className={`rp-check ${rf.stopSell ? "on" : ""}`}>
                <input type="checkbox" checked={rf.stopSell} onChange={(e) => setR("stopSell", e.target.checked)} />
                Stop sell on these dates (no bookings on this plan)
              </label>
              <div className="rp-field" style={{ marginBottom: 0 }}>
                <input type="text" aria-label="Notes" value={rf.notes} placeholder="Notes (only visible to you)" onChange={(e) => setR("notes", e.target.value)} />
              </div>
            </div>

            <div className="rp-foot">
              {range.start && <span className="rp-apply">Applies to {applyCount} {applyCount === 1 ? "day" : "days"}</span>}
              <button type="button" className="rp-btn" onClick={resetRuleForm} disabled={savingRule}>{editingRule ? "Cancel edit" : "Clear"}</button>
              <button type="submit" className="rp-btn rp-main" disabled={savingRule}>
                {savingRule ? "Saving..." : editingRule ? "Update date price" : "Save date price"}
              </button>
            </div>
          </form>

          <section className="rp-card rp-table-card">
            <div className="rp-table-head"><h2 className="rp-sec first">Date prices</h2></div>
            {loadingRules ? (
              <div className="rp-state flat"><div className="rp-spin" /></div>
            ) : rules.length === 0 ? (
              <div className="rp-state flat"><p>No date prices yet. Select dates on the calendar to add one.</p></div>
            ) : (
              <div className="rp-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Dates</th>
                      <th>Type</th>
                      <th className="num">Price</th>
                      <th className="num">Priority</th>
                      <th>Stay</th>
                      <th>Status</th>
                      <th className="right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rules.map((r) => {
                      const s = rowDay(r.startDate);
                      const e = rowDay(r.endDate);
                      const past = e < today;
                      const asking = deleteRuleId === r._id;
                      return (
                        <tr key={r._id} className={past || !r.isActive ? "dim" : ""}>
                          <td className="nowrap"><strong>{fmtDate(s)}{e !== s ? ` to ${fmtDate(e)}` : ""}</strong>{past && <em className="rp-tag">Past</em>}</td>
                          <td>{TYPE_LABELS[r.pricingType] || r.pricingType}{r.title ? <small>{r.title}</small> : null}</td>
                          <td className="num"><strong>{inr(r.price)}</strong></td>
                          <td className="num">{r.priority}</td>
                          <td>{r.minStay || r.maxStay ? `${r.minStay ?? "-"} to ${r.maxStay ?? "-"} nights` : <span className="rp-muted">Plan default</span>}</td>
                          <td>
                            {r.stopSell ? <span className="rp-badge stop">Stop sell</span> : <span className={`rp-badge ${r.isActive ? "on" : "off"}`}>{r.isActive ? "Active" : "Inactive"}</span>}
                          </td>
                          <td className="right">
                            {asking ? (
                              <span className="rp-confirm">
                                Delete?
                                <button type="button" className="rp-btn dsolid sm" disabled={deletingRule} onClick={() => confirmDeleteRule(r._id)}>{deletingRule ? "..." : "Yes"}</button>
                                <button type="button" className="rp-btn sm" disabled={deletingRule} onClick={() => setDeleteRuleId(null)}>No</button>
                              </span>
                            ) : (
                              <span className="rp-acts">
                                <button type="button" className="rp-btn sm" onClick={() => toggleRule(r)}>{r.isActive ? "Turn off" : "Turn on"}</button>
                                <button type="button" className="rp-btn sm" onClick={() => editRule(r)}>Edit</button>
                                <button type="button" className="rp-btn sm danger" onClick={() => setDeleteRuleId(r._id)}>Delete</button>
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}

      {/* ============ DELETE PLAN DIALOG ============ */}
      {deletePlan && (
        <div className="rp-overlay" onMouseDown={() => !deleting && setDeletePlan(null)}>
          <div className="rp-dialog" role="alertdialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
            <h2>Delete this rate plan?</h2>
            <p>&ldquo;{deletePlan.name}&rdquo; and all its date prices will be removed. This cannot be undone. To pause it instead, switch it to inactive.</p>
            <div className="rp-dialog-acts">
              <button type="button" className="rp-btn" onClick={() => setDeletePlan(null)} disabled={deleting}>Keep plan</button>
              <button type="button" className="rp-btn dsolid" onClick={confirmDeletePlan} disabled={deleting}>{deleting ? "Deleting..." : "Delete plan"}</button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className={`rp-toast ${toast.type}`} role="status">{toast.msg}</div>}
    </div>
  );
}

/* ==============================
   STYLES (class names rp- prefix: global CSS se takrayenge nahi)
============================== */
const css = `
@import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=Outfit:wght@400;500;600&display=swap');
.rp{--ink:#0f172a;--muted:#64748b;--line:#e2e8f0;--bg:#f5faff;--accent:#0369a1;--accent-d:#075985;--soft:#e0f2fe;--danger:#dc2626;--green:#15803d;
  font-family:'Outfit',system-ui,sans-serif;color:var(--ink);background:var(--bg);min-height:100vh;max-width:1200px;margin:0 auto;padding:32px 24px}
.rp *{box-sizing:border-box}
.rp h1,.rp h2,.rp h3{font-family:'Syne',sans-serif;margin:0}
.rp-head{display:flex;justify-content:space-between;align-items:flex-start;gap:16px;flex-wrap:wrap;margin-bottom:20px}
.rp-head h1,.rp-title{font-size:30px;color:#0c4a6e}
.rp-head p{margin:6px 0 0;color:var(--muted);font-size:14px;max-width:560px}
.rp-title .rp-code{vertical-align:middle;margin-left:8px}

/* buttons */
.rp .rp-btn{display:inline-flex;align-items:center;justify-content:center;gap:6px;white-space:nowrap;flex-shrink:0;width:auto;height:auto;min-height:0;line-height:1.2;border:1px solid var(--line);background:#fff;color:var(--ink);padding:10px 18px;border-radius:8px;font:600 14px 'Outfit',sans-serif;cursor:pointer}
.rp .rp-btn:hover:not(:disabled){border-color:#b6c4d3}
.rp .rp-btn.sm{padding:6px 12px;font-size:13px}
.rp .rp-btn.soft{background:var(--soft);border-color:#bae6fd;color:var(--accent)}
.rp .rp-btn.danger{color:var(--danger);border-color:#f3c4c4}
.rp .rp-btn.dsolid{background:var(--danger);border-color:var(--danger);color:#fff}
.rp .rp-btn.rp-main{background:var(--accent);border-color:var(--accent);color:#fff;min-height:40px;overflow:visible;position:static}
.rp .rp-btn.rp-main:hover:not(:disabled){background:var(--accent-d);border-color:var(--accent-d)}
.rp .rp-btn.rp-main.sm{min-height:0}
.rp .rp-btn.rp-add{padding:12px 24px;font-size:15px;min-height:44px;min-width:170px}
.rp .rp-btn:disabled{opacity:.45;cursor:not-allowed}
.rp button:focus-visible,.rp input:focus-visible,.rp select:focus-visible{outline:2px solid var(--accent);outline-offset:2px}

/* cards + fields */
.rp-card{background:#fff;border-radius:16px;box-shadow:0 2px 14px rgba(15,23,42,.07);padding:22px 24px;margin-bottom:20px}
.rp-toolbar{display:flex;gap:16px;align-items:flex-end;flex-wrap:wrap}
.rp-toolbar .rp-field{margin-bottom:0}
.rp-grow{flex:1;min-width:240px}
.rp-mini{background:#f0f9ff;border-radius:12px;padding:10px 16px;font-size:12px;color:var(--muted)}
.rp-mini span{display:block;font:700 18px 'Syne',sans-serif;color:#0c4a6e}
.rp-field{display:flex;flex-direction:column;gap:6px;margin-bottom:14px}
.rp-field label{font-size:13px;font-weight:600;color:#334155}
.rp input[type=number],.rp input[type=date],.rp input[type=text],.rp select{width:100%;padding:10px 13px;border:1.5px solid var(--line);border-radius:8px;font:14px 'Outfit',sans-serif;background:#fff;color:var(--ink)}
.rp input:focus,.rp select:focus{border-color:var(--accent);box-shadow:0 0 0 3px rgba(3,105,161,.12)}
.rp-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:0 16px}
.rp-grid.three{grid-template-columns:repeat(3,1fr)}
.rp-grid.four{grid-template-columns:repeat(4,1fr)}
.rp-sec{font-size:17px;color:#0c4a6e;margin:22px 0 12px}
.rp-sec.first{margin-top:0}
.rp-hint{font-size:13px;color:var(--muted);margin:6px 0 14px}
.rp-check{display:flex;align-items:center;gap:10px;padding:12px 14px;border:1.5px solid var(--line);border-radius:10px;font-size:14px;cursor:pointer;margin-bottom:14px}
.rp-check.on{background:#f0f9ff;border-color:#7dd3fc}
.rp-check input{width:16px;height:16px;accent-color:var(--accent)}
.rp-foot{display:flex;justify-content:flex-end;align-items:center;gap:10px;flex-wrap:wrap;margin-top:10px;padding-top:16px;border-top:1px solid var(--soft)}
.rp-apply{margin-right:auto;font-size:13px;color:var(--muted);font-weight:500}
.rp-error{background:#fee2e2;color:#b91c1c;border:1px solid #fecaca;border-radius:8px;padding:10px 14px;font-size:14px;margin:12px 0;display:flex;gap:10px;align-items:center;flex-wrap:wrap}
.rp-state{background:#fff;border:1px dashed #bcd7ea;border-radius:14px;padding:56px 20px;text-align:center;color:var(--muted);display:flex;flex-direction:column;align-items:center;gap:14px}
.rp-state.flat{border:0;padding:36px 20px}
.rp-state p{margin:0}
.rp-ico{width:60px;height:60px;border-radius:50%;background:var(--soft);color:var(--accent);display:grid;place-items:center;font:700 26px 'Syne',sans-serif}
.rp-spin{width:38px;height:38px;border:3px solid #bae6fd;border-top-color:var(--accent);border-radius:50%;animation:rp-spin 1s linear infinite}
@keyframes rp-spin{to{transform:rotate(360deg)}}
.rp-wrap{max-width:900px;margin:0 auto}
.rp-wrap.wide{max-width:1000px}
.rp-back{background:none;border:0;padding:6px 0;margin-bottom:12px;font:600 14px 'Outfit',sans-serif;color:var(--accent);cursor:pointer}
.rp-back:hover{text-decoration:underline}

/* plan cards */
.rp-plans{display:grid;grid-template-columns:repeat(auto-fill,minmax(330px,1fr));gap:18px}
.rp-plan{background:#fff;border-radius:16px;box-shadow:0 2px 14px rgba(15,23,42,.07);padding:20px;display:flex;flex-direction:column;gap:10px;border:1.5px solid transparent}
.rp-plan.off{opacity:.75;background:#fafcfe}
.rp-plan-top{display:flex;justify-content:space-between;align-items:flex-start;gap:12px}
.rp-plan-title{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.rp-plan-title h3{font-size:18px;color:#0c4a6e}
.rp-code{font:600 11px 'Outfit',sans-serif;background:var(--soft);color:var(--accent-d);padding:3px 9px;border-radius:999px;letter-spacing:.04em}
.rp-off{font:600 11px 'Outfit',sans-serif;background:#e2e8f0;color:#475569;padding:3px 9px;border-radius:999px}
.rp-price{display:flex;align-items:baseline;gap:8px;flex-wrap:wrap}
.rp-price strong{font:700 24px 'Syne',sans-serif;color:var(--accent-d)}
.rp-price span{font-size:13px;color:var(--muted)}
.rp-price em{font-style:normal;font-size:12px;font-weight:600;background:#fef3c7;color:#92400e;padding:2px 9px;border-radius:999px}
.rp-facts{list-style:none;margin:0;padding:0;display:flex;flex-wrap:wrap;gap:6px}
.rp-facts li{font-size:12px;font-weight:500;background:#f1f5f9;color:#475569;padding:4px 10px;border-radius:999px}
.rp-cancel{margin:0;font-size:13px;color:#166534;font-weight:500}
.rp-desc{margin:0;font-size:13px;color:var(--muted);line-height:1.5}
.rp-plan-foot{display:flex;justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap;margin-top:auto;padding-top:14px;border-top:1px solid #eef3f8}
.rp-acts{display:inline-flex;gap:6px}

/* switch */
.rp-switch{position:relative;display:inline-block;cursor:pointer;flex:none}
.rp-switch input{position:absolute;opacity:0;width:0;height:0}
.rp-track{display:block;width:40px;height:22px;border-radius:999px;background:#c5d0da;position:relative;transition:background .2s}
.rp-track::after{content:"";position:absolute;top:3px;left:3px;width:16px;height:16px;border-radius:50%;background:#fff;transition:transform .2s;box-shadow:0 1px 3px rgba(0,0,0,.25)}
.rp-switch input:checked + .rp-track{background:var(--green)}
.rp-switch input:checked + .rp-track::after{transform:translateX(18px)}
.rp-switch input:focus-visible + .rp-track{outline:2px solid var(--accent);outline-offset:2px}

/* calendar */
.rp-cal-top{display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:12px}
.rp-monthnav{display:flex;align-items:center;gap:10px}
.rp-monthnav strong{min-width:150px;text-align:center;font-family:'Syne',sans-serif;font-size:17px}
.rp-legend{display:flex;align-items:center;gap:6px 14px;flex-wrap:wrap;font-size:12px;color:var(--muted)}
.rp .lg{display:inline-block;width:12px;height:12px;border-radius:3px;margin-right:4px;vertical-align:-1px;border:1px solid #cbd5e1}
.rp .lg.base{background:#fff}
.rp .lg.weekend{background:#fef3c7;border-color:#fcd34d}
.rp .lg.rule{background:#dcfce7;border-color:#86efac}
.rp .lg.stop{background:#e2e8f0;border-color:#94a3b8}
.rp-cal{display:grid;grid-template-columns:repeat(7,minmax(0,1fr));gap:6px}
.rp-cal.loading{opacity:.5;pointer-events:none}
.rp-dow{text-align:center;font-size:12px;font-weight:600;color:var(--muted);padding:4px 0}
.rp-cell{min-height:76px;border:1.5px solid var(--line);border-radius:10px;background:#fff;padding:7px 8px;display:flex;flex-direction:column;gap:2px;text-align:left;font-family:'Outfit',sans-serif;cursor:pointer;color:var(--ink)}
.rp-cell.blank{border:0;background:none;cursor:default}
.rp-cell:hover:not(:disabled):not(.blank){border-color:var(--accent)}
.rp-cell.weekend{background:#fffbeb;border-color:#fde68a}
.rp-cell.rule{background:#f0fdf4;border-color:#bbf7d0}
.rp-cell.stop{background:#f1f5f9;border-color:#cbd5e1}
.rp-cell.sel{box-shadow:inset 0 0 0 2px var(--accent);background:var(--soft)}
.rp-cell.edge{background:#bae6fd}
.rp-cell.today .rp-day{background:var(--accent);color:#fff;border-radius:999px;padding:0 7px}
.rp-cell.past{opacity:.4;cursor:not-allowed}
.rp-day{font-weight:600;font-size:13px;align-self:flex-start}
.rp-cp{font-size:12px;font-weight:600;color:#0c4a6e}
.rp-cell.rule .rp-cp{color:#166534}
.rp-cell.stop .rp-cp{color:#475569}
.rp-ct{font-size:10px;color:#94a3b8;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.rp-cal-acts{display:flex;gap:8px;margin-top:14px;flex-wrap:wrap}

/* rules table */
.rp-table-card{padding:0;overflow:hidden}
.rp-table-head{padding:20px 24px 12px}
.rp-scroll{overflow-x:auto}
.rp table{width:100%;border-collapse:collapse;min-width:860px}
.rp th{padding:12px 16px;text-align:left;font-size:12px;font-weight:600;color:var(--accent);background:#f0f9ff;border-bottom:1px solid var(--soft);white-space:nowrap}
.rp td{padding:12px 16px;font-size:14px;border-bottom:1px solid #f0f6fb;vertical-align:middle}
.rp tbody tr:last-child td{border-bottom:0}
.rp tbody tr:hover{background:#f8fcff}
.rp .num{text-align:right}
.rp .right{text-align:right}
.rp .nowrap{white-space:nowrap}
.rp tr.dim td{color:#94a3b8}
.rp td small{display:block;font-size:12px;color:var(--muted);margin-top:2px}
.rp-muted{color:var(--muted)}
.rp-tag{font-style:normal;font-size:11px;font-weight:600;background:#e2e8f0;color:#475569;padding:2px 8px;border-radius:999px;margin-left:8px}
.rp-badge{font-size:12px;font-weight:600;padding:3px 11px;border-radius:999px;display:inline-block}
.rp-badge.on{background:#dcfce7;color:#166534}
.rp-badge.off{background:#e2e8f0;color:#475569}
.rp-badge.stop{background:#fee2e2;color:#b91c1c}
.rp-confirm{display:inline-flex;align-items:center;gap:8px;flex-wrap:wrap;font-size:13px;color:#b91c1c;font-weight:600}

/* dialog + toast */
.rp-overlay{position:fixed;inset:0;background:rgba(15,23,42,.55);display:flex;align-items:center;justify-content:center;padding:20px;z-index:1000}
.rp-dialog{background:#fff;border-radius:16px;width:100%;max-width:440px;padding:26px;box-shadow:0 24px 70px rgba(15,23,42,.3)}
.rp-dialog h2{font-size:21px;margin-bottom:10px}
.rp-dialog p{margin:0 0 20px;font-size:14px;line-height:1.6;color:#475569}
.rp-dialog-acts{display:flex;justify-content:flex-end;gap:8px;flex-wrap:wrap}
.rp-toast{position:fixed;top:20px;right:20px;z-index:2000;padding:12px 18px;border-radius:10px;font-size:14px;font-weight:500;color:#fff;background:var(--green);box-shadow:0 6px 24px rgba(15,23,42,.2)}
.rp-toast.err{background:var(--danger)}

@media (max-width:760px){
  .rp{padding:20px 12px}
  .rp-card{padding:16px 14px}
  .rp-head{flex-direction:column;align-items:stretch}
  .rp .rp-btn.rp-add{width:100%}
  .rp-grid,.rp-grid.three,.rp-grid.four{grid-template-columns:1fr 1fr}
  .rp-cal{gap:3px}
  .rp-cell{min-height:60px;padding:4px}
  .rp-ct{display:none}
  .rp-cp{font-size:10px}
  .rp-plans{grid-template-columns:1fr}
}
@media (max-width:480px){.rp-grid,.rp-grid.three,.rp-grid.four{grid-template-columns:1fr}}
@media (prefers-reduced-motion:reduce){.rp-spin{animation-duration:3s}.rp-track,.rp-track::after{transition:none}}
`;